"use client";

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import AppLogo from '@/components/ui/AppLogo';

function useInView(threshold = 0.12) {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {setInView(true);obs.disconnect();}},
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const values = [
{ title: '傾聽', titleEn: 'Listen', desc: '每個項目的第一步，是真正聆聽你的生活方式，而不是急於展示我們的設計。', icon: '◎' },
{ title: '誠信', titleEn: 'Integrity', desc: '報價透明、時程誠實、材料如實說明。我們相信長期信任勝過一次完美的推銷。', icon: '◈' },
{ title: '精工', titleEn: 'Craft', desc: '每個細節都值得認真對待。從規劃到完工，我們以工匠精神對待每一個決定。', icon: '◇' },
{ title: '陪伴', titleEn: 'Partnership', desc: '設計不是一次性交付，而是一段關係。我們在你入住後仍然在場。', icon: '◉' }];


const milestones = [
{ year: '2017', event: '工作室創立於香港上環，從第一個住宅翻新案例開始。' },
{ year: '2019', event: '擴展至商業空間設計，完成首個品牌咖啡廳項目。' },
{ year: '2021', event: '引入永續設計理念，與環保材料供應商建立長期合作。' },
{ year: '2023', event: '完成第100個項目，客戶滿意度達98%。' },
{ year: '2025', event: '持續深耕以人為本的設計哲學，服務更多香港家庭與品牌。' }];


export default function About() {
  const sec1 = useInView();
  const sec2 = useInView();
  const sec3 = useInView();
  const sec4 = useInView();

  return (
    <>
      <Header />

      {/* ── MINIMAL PAGE HEADER ──────────────────────────── */}
      <section className="pt-32 pb-16 bg-background border-b border-border">
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="tag-pill">About Us</span>
          </div>
          <h1 className="font-heading font-700 text-h1 text-primary mb-3">
            關於我們
          </h1>
          <p className="font-body text-base text-secondary max-w-lg">
            安日室內設計工作室——以人為本，用設計記錄每一個日常的儀式感。
          </p>
        </div>
      </section>

      {/* ── ORIGIN STORY ─────────────────────────────────── */}
      <section
        ref={sec1.ref as React.RefObject<HTMLElement>}
        className="py-24 bg-background">
        
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div
              className="relative overflow-hidden"
              style={{
                borderRadius: '4px',
                opacity: sec1.inView ? 1 : 0,
                transform: sec1.inView ? 'translateX(0)' : 'translateX(-40px)',
                transition: 'opacity 0.9s ease, transform 0.9s ease'
              }}>
              
              <div className="relative aspect-[4/3]">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1e5b08612-1766558211927.png"
                  alt="Anri Design Studio workspace — design process and planning"
                  fill
                  className="object-cover" />
                
                {/* Gold accent border */}
                <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-accent/30 pointer-events-none" style={{ borderRadius: '2px' }} />
              </div>
              {/* Floating logo card */}
              <div
                className="absolute -bottom-6 -left-6 glass-panel p-4 shadow-lg animate-float hidden md:block"
                style={{ borderRadius: '4px' }}>
                
                <div className="flex items-center gap-3">
                  <AppLogo
                    src="/assets/images/beb20884-3324-4758-9eef-42340ec5b17d-1773145188404.jpg"
                    size={40} />
                  
                  <div>
                    <p className="font-heading text-xs font-700 text-primary">Anri Design</p>
                    <p className="font-mono text-[10px] text-muted">Est. 2017 · 上環</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Text */}
            <div
              style={{
                opacity: sec1.inView ? 1 : 0,
                transform: sec1.inView ? 'translateX(0)' : 'translateX(40px)',
                transition: 'opacity 0.9s ease 0.15s, transform 0.9s ease 0.15s'
              }}>
              
              <span className="tag-pill mb-6 inline-block">Our Story</span>
              <h2 className="font-heading font-700 text-h2 text-primary mb-6">
                為什麼叫「安日」？
              </h2>
              <div className="space-y-5 font-body text-base text-secondary leading-relaxed">
                <p>
                  「安」代表安心、安靜、安居——我們希望每一個設計，都讓你在回到家的瞬間，
                  感受到一種真實的放鬆。「日」代表日常、日子、日積月累的生活。
                </p>
                <p>
                  2017年，我們在香港上環創立這個工作室，從第一個住宅翻新案例開始，
                  就確立了一個信念：好的設計，不是炫技，而是讓你的日常更美好。
                </p>
                <p>
                  八年來，我們服務了超過120個家庭與品牌，每一個項目都從傾聽開始，
                  從你的生活習慣、家庭成員、使用情境出發，打造真正屬於你的空間。
                </p>
              </div>
              <div className="mt-8 flex items-center gap-3">
                <div className="w-8 h-px bg-accent" />
                <span className="font-mono text-xs text-muted tracking-wide">安日室內設計工作室 · 2017–2025</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES GRID ───────────────────────────────────── */}
      <section
        ref={sec2.ref as React.RefObject<HTMLElement>}
        className="py-24 bg-surface">
        
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div
            className="mb-16"
            style={{
              opacity: sec2.inView ? 1 : 0,
              transform: sec2.inView ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease'
            }}>
            
            <span className="tag-pill mb-4 inline-block">Core Values</span>
            <h2 className="font-heading font-700 text-h2 text-primary">我們的核心價值</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((v, i) =>
            <div
              key={v.title}
              className="bg-background p-8 group hover:bg-primary transition-colors duration-300 hover-shine"
              style={{
                borderRadius: '4px',
                opacity: sec2.inView ? 1 : 0,
                transform: sec2.inView ? 'translateY(0)' : 'translateY(40px)',
                transition: `opacity 0.8s ease ${i * 0.1}s, transform 0.8s ease ${i * 0.1}s`
              }}>
              
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="font-heading text-xl font-700 text-primary group-hover:text-white transition-colors">
                      {v.title}
                    </h3>
                    <p className="font-mono text-xs text-accent/70 mt-1">{v.titleEn}</p>
                  </div>
                  <span className="text-3xl text-accent/40 group-hover:text-accent transition-colors">{v.icon}</span>
                </div>
                <p className="font-body text-sm text-secondary group-hover:text-white/60 leading-relaxed transition-colors">
                  {v.desc}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── TIMELINE ─────────────────────────────────────── */}
      <section
        ref={sec3.ref as React.RefObject<HTMLElement>}
        className="py-24 bg-background">
        
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div
            className="mb-16"
            style={{
              opacity: sec3.inView ? 1 : 0,
              transform: sec3.inView ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease'
            }}>
            
            <span className="tag-pill mb-4 inline-block">Journey</span>
            <h2 className="font-heading font-700 text-h2 text-primary">我們的成長歷程</h2>
          </div>

          {/* Horizontal scroll timeline */}
          <div className="relative">
            {/* Line */}
            <div className="absolute top-6 left-0 right-0 h-px bg-border hidden md:block" />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {milestones.map((m, i) =>
              <div
                key={m.year}
                className="relative"
                style={{
                  opacity: sec3.inView ? 1 : 0,
                  transform: sec3.inView ? 'translateY(0)' : 'translateY(30px)',
                  transition: `opacity 0.8s ease ${i * 0.12}s, transform 0.8s ease ${i * 0.12}s`
                }}>
                
                  {/* Dot */}
                  <div className="w-3 h-3 rounded-full bg-accent mb-6 relative z-10 hidden md:block" />
                  <span className="font-heading text-2xl font-700 text-accent block mb-3 md:hidden">{m.year}</span>
                  <span className="font-mono text-xs text-accent tracking-wide mb-2 block hidden md:block">{m.year}</span>
                  <p className="font-body text-sm text-secondary leading-relaxed">{m.event}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── BRIDGE CTA ───────────────────────────────────── */}
      <section
        ref={sec4.ref as React.RefObject<HTMLElement>}
        className="py-20 bg-surface border-t border-border">
        
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div
            className="flex flex-col md:flex-row items-center justify-between gap-8"
            style={{
              opacity: sec4.inView ? 1 : 0,
              transform: sec4.inView ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease'
            }}>
            
            <div>
              <h3 className="font-heading text-h3 font-700 text-primary mb-2">
                想了解我們能為你做什麼？
              </h3>
              <p className="font-body text-sm text-secondary">
                查看我們完整的業務範圍，了解住宅與商業設計的詳細服務內容。
              </p>
            </div>
            <div className="flex gap-4 flex-shrink-0">
              <Link href="/business-scope" className="btn-outline">
                查看業務範圍
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
              <Link href="/registration-center" className="btn-accent">
                免費諮詢
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>);

}